import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Heart, Truck, Award, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ProductReviews from "@/components/ProductReviews";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [related, setRelated] = useState([]);
  const { currency } = useCurrency();
  const { addItem } = useCart();
  const { user, toggleWishlist } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${slug}`);
        setProduct(data);
        setSize((data.variants && data.variants[0]?.size) || "");
        const { data: rel } = await api.get(`/products?category=${data.category}&limit=4`);
        setRelated(rel.filter((r) => r.product_id !== data.product_id).slice(0, 4));
      } catch {
        setProduct(null);
      }
    })();
  }, [slug]);

  if (!product) return <div className="pt-40 pb-40 text-center text-[#7A7A7A]" data-testid="pdp-loading">Loading…</div>;

  const inWishlist = user?.wishlist?.includes(product.product_id);

  const onAdd = () => {
    if (!size) { toast.error("Select a size."); return; }
    addItem(product, size, 1);
    toast.success("Added to your bag.");
  };
  const onWish = async () => {
    if (!user) { toast.error("Sign in to save to wishlist."); return; }
    await toggleWishlist(product.product_id);
    toast.success(inWishlist ? "Removed from wishlist." : "Saved to wishlist.");
  };

  return (
    <main className="pt-24 md:pt-32 pb-16 md:pb-24" data-testid="pdp">
      <div className="grid md:grid-cols-12 gap-8 md:gap-14 px-6 md:px-12 lg:px-16">
        {/* Gallery */}
        <div className="md:col-span-7 space-y-4">
          {(product.images || []).map((src, i) => (
            <div key={i} className="aspect-[4/5] overflow-hidden bg-[#F3EFEA]" data-testid={`pdp-image-${i}`}>
              <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>

        {/* Details (sticky on desktop) */}
        <div className="md:col-span-5">
          <div className="md:sticky md:top-32">
            <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">{product.category} · {(product.occasion || []).join(" · ")}</p>
            <h1 className="font-serif text-4xl md:text-5xl mt-3">{product.name}</h1>
            <p className="mt-3 text-[15px] text-[#4A4A4A] italic">{product.tagline}</p>

            <div className="mt-6 flex items-baseline gap-3">
              <p className="text-2xl text-[#1A1A1A]" data-testid="pdp-price">{formatPrice(product.price_inr, currency)}</p>
              {product.compare_at_price_inr && (
                <p className="text-sm line-through text-[#7A7A7A]">{formatPrice(product.compare_at_price_inr, currency)}</p>
              )}
            </div>

            {/* Size */}
            <div className="mt-8">
              <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mb-3">Select Size</p>
              <div className="flex flex-wrap gap-2">
                {(product.variants || []).map((v) => (
                  <button
                    key={v.size}
                    onClick={() => setSize(v.size)}
                    className={`px-4 py-2 text-sm border transition-colors ${
                      size === v.size
                        ? "bg-[#5C1A1B] text-[#FDFBF7] border-[#5C1A1B]"
                        : "border-[#BCA37F]/60 text-[#1A1A1A] hover:border-[#5C1A1B]"
                    } ${v.stock === 0 ? "opacity-40" : ""}`}
                    disabled={v.stock === 0}
                    data-testid={`pdp-size-${v.size}`}
                  >
                    {v.size}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={onAdd} className="btn-editorial flex-1 min-w-[220px]" data-testid="pdp-add-to-cart">
                Add to Bag
              </button>
              <button onClick={onWish} className={`px-4 py-3 border transition-colors ${inWishlist ? "bg-[#5C1A1B] text-[#FDFBF7] border-[#5C1A1B]" : "border-[#5C1A1B] text-[#5C1A1B] hover:bg-[#5C1A1B] hover:text-[#FDFBF7]"}`} data-testid="pdp-wishlist">
                <Heart size={18} strokeWidth={1.4} fill={inWishlist ? "currentColor" : "none"} />
              </button>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-nav text-[#7A7A7A]">
              <span className="flex items-center gap-2"><Truck size={12} /> Ships worldwide</span>
              <span className="flex items-center gap-2"><Award size={12} /> Handcrafted</span>
              <span className="flex items-center gap-2"><RefreshCw size={12} /> Easy returns</span>
            </div>

            <div className="mt-10">
              <Accordion type="multiple" className="w-full">
                <AccordionItem value="desc">
                  <AccordionTrigger className="uppercase tracking-nav text-[11px] text-[#1A1A1A]" data-testid="pdp-acc-description">
                    Description
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#4A4A4A] leading-relaxed">
                    {product.description}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="fabric">
                  <AccordionTrigger className="uppercase tracking-nav text-[11px] text-[#1A1A1A]" data-testid="pdp-acc-fabric">
                    Fabric & Craft
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#4A4A4A] leading-relaxed">
                    <p><strong>Fabric:</strong> {product.fabric || "—"}</p>
                    <p><strong>Colour:</strong> {product.color || "—"}</p>
                    <p><strong>Craft:</strong> {(product.craft || []).join(", ") || "—"}</p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="care">
                  <AccordionTrigger className="uppercase tracking-nav text-[11px] text-[#1A1A1A]" data-testid="pdp-acc-care">
                    Care
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#4A4A4A] leading-relaxed">{product.care || "Dry clean only."}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="size">
                  <AccordionTrigger className="uppercase tracking-nav text-[11px] text-[#1A1A1A]" data-testid="pdp-acc-size">
                    Size Chart & Fit
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#4A4A4A] leading-relaxed">
                    <p>{product.size_chart || "Standard Indian sizing. Contact us for bespoke measurements."}</p>
                    <p className="mt-2 italic">{product.model_note}</p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="px-6 md:px-12 lg:px-16">
        <ProductReviews slug={product.slug} productId={product.product_id} />
      </section>

      {/* Complete the Look */}
      {related.length > 0 && (
        <section className="mt-24 px-6 md:px-12 lg:px-16" data-testid="pdp-related">
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-3">Complete the Look</p>
          <h3 className="font-serif text-3xl mb-10">Pieces that pair with this.</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-10">
            {related.map((r) => (
              <Link key={r.product_id} to={`/product/${r.slug}`} className="product-card group" data-testid={`related-${r.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden bg-[#F3EFEA]">
                  <img src={r.images[0]} alt={r.name} className="img-primary w-full h-full object-cover" loading="lazy" />
                </div>
                <p className="font-serif text-lg mt-3">{r.name}</p>
                <p className="text-sm text-[#4A4A4A]">{formatPrice(r.price_inr, currency)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
